import { useState } from 'react';
import { TextField, Autocomplete, Box } from '@mui/material';

/**
 * Generic multi-select "picker" component, reusable anywhere a list of
 * options needs multi-selection (client accounts, platforms, themes, tags, etc.).
 *
 * Pattern: after picking an option, the input clears itself (it's a picker,
 * not a persistent multi-value field) - but while the user is typing, normal
 * MUI filtering applies. The parent owns the actual selected array and is
 * responsible for rendering it (e.g. as removable chips) outside this
 * component - see "theme-list-container-filter" / "theme-container" usage
 * elsewhere in the app for a ready-made chip style.
 *
 * Works out of the box with a plain array of strings. For arrays of objects,
 * pass getOptionLabel and isOptionEqualToValue.
 *
 * Props:
 * - options: array                        -> full list of available options
 * - selectedValues: array                 -> currently selected values (controlled by parent)
 * - onSelect(newValue)                    -> called with a single newly picked option
 * - getOptionLabel?(option): string       -> how to display an option (default: identity, works for strings)
 * - isOptionEqualToValue?(option, value)  -> equality check between an option and a selected value (default: strict ===)
 * - disabled?: boolean
 * - placeholder?: string
 */
export const MultiSelectPicker = ({
    options,
    selectedValues,
    onSelect,
    getOptionLabel = (option) => option,
    isOptionEqualToValue = (option, value) => option === value,
    disabled,
    placeholder,
}) => {
    const [inputValue, setInputValue] = useState('');

    const isSelected = (option) =>
        selectedValues?.some((v) => isOptionEqualToValue(option, v));

    return (
        <Autocomplete
            value={null}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue, reason) => {
                // Keep normal typing behaviour (so MUI's default filtering works),
                // but don't leave leftover text in the box after a pick or a blur.
                if (reason === 'input') {
                    setInputValue(newInputValue);
                } else {
                    setInputValue('');
                }
            }}
            limitTags={0}
            sx={{
                '& .MuiAutocomplete-inputRoot': {
                    height: '40px',
                },
            }}
            disabled={disabled || !options || options.length <= 0}
            onChange={(_event, newValue) => {
                if (newValue !== null && newValue !== undefined && !isSelected(newValue)) {
                    onSelect(newValue);
                }
                setInputValue('');
            }}
            options={options || []}
            getOptionLabel={getOptionLabel}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    placeholder={placeholder}
                    sx={{ height: '40px' }}
                />
            )}
            renderOption={(props, option) => {
                const selected = isSelected(option);
                return (
                    <li
                        {...props}
                        style={{
                            backgroundColor: selected ? '#ffe426' : 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box>{getOptionLabel(option)}</Box>
                        {selected && <img src="Images/icon-check.png" alt="icon-check" />}
                    </li>
                );
            }}
        />
    );
};

export default MultiSelectPicker;